/**
 * TrainingRecordsTab Component
 * Displays onboard training records
 */

import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  GraduationCap,
  PlusCircle,
  Calendar,
  User,
  Users,
  Award,
  Download,
  Settings,
  Clock,
  Search
} from 'lucide-react';
import { Badge } from '../../../catalyst/badge';
import TrainingSidebar from './TrainingSidebar';
// FT-358: middle-slot actions registered via context; the shell owns the bar.
import { useSetBottomBarActions } from '../../../../contexts/MobileBottomBarActionsContext';
import { useTrainingData } from '../../../../hooks/domain/useTrainingData';
import { TRAINING_CATEGORIES, COMPETENCY_LEVELS } from '../../../../config/training';
import Dropdown from '../../../common/Dropdown';

const TrainingRecordsTab = ({ 
  trainingRecords: propRecords, // Keep prop for backward compatibility
  crew, 
  onAddTraining, 
  onViewTraining 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [topicTab, setTopicTab] = useState('all');  // Topic filtering via tabs
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dateRange, setDateRange] = useState('all');
  
  // Use new hook and fall back to prop if needed
  const { trainingRecords: hookRecords, stats } = useTrainingData();
  const trainingRecords = hookRecords.length > 0 || !propRecords ? hookRecords : propRecords;

  // Get unique training topics
  const trainingTopics = useMemo(() => {
    const topics = new Set(trainingRecords.map(t => t.topic));
    return Array.from(topics).sort();
  }, [trainingRecords]);

  // Pre-compute category counts once for filter dropdown
  const categoryCounts = useMemo(() => {
    const counts = {};
    trainingRecords.forEach(r => {
      if (r.category) {
        counts[r.category] = (counts[r.category] || 0) + 1;
      }
    });
    return counts;
  }, [trainingRecords]);

  // Helper to get trainee names
  const getTraineeNames = (record) => {
    if (record.trainees && record.trainees.length > 0) {
      return record.trainees.map(t => t.name).join(', ');
    }
    // Fallback for legacy records
    if (record.trainee) {
      return record.trainee;
    }
    return 'Unknown';
  };
  
  // Filter training records
  const filteredRecords = useMemo(() => {
    return trainingRecords.filter(record => {
      // Category filter
      if (categoryFilter && record.category !== categoryFilter) return false;

      // Date range filter
      if (dateRange !== 'all') {
        const recordDate = new Date(record.date);
        const daysAgo = parseInt(dateRange);
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
        if (recordDate < cutoffDate) return false;
      }

      // Search filter
      const traineeNames = getTraineeNames(record).toLowerCase();
      const matchesSearch = searchTerm === '' ||
        traineeNames.includes(searchTerm.toLowerCase()) ||
        record.topic?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesTopic = topicTab === 'all' || record.topic === topicTab;

      return matchesSearch && matchesTopic;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [trainingRecords, searchTerm, topicTab, categoryFilter, dateRange]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Calculate pagination
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredRecords.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredRecords, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, topicTab, categoryFilter, dateRange]);

  // Check if any filters are active
  const hasActiveFilters = searchTerm !== '' || categoryFilter !== '' || topicTab !== 'all' || dateRange !== 'all';

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setTopicTab('all');
    setDateRange('all');
  };

  // Handle quick actions from sidebar
  const handleQuickAction = (action) => {
    if (action === 'add') {
      onAddTraining();
    } else if (action === 'export') {
      toast('Training records export coming soon!', { duration: 3000, icon: '📥' });
    } else if (action === 'history') {
      toast('Training history view coming soon!', { duration: 3000, icon: '📜' });
    } else if (action === 'settings') {
      toast('Training settings coming soon!', { duration: 3000, icon: '⚙️' });
    }
  };

  // FT-358: register middle-slot bottom-bar actions per brief §6
  // (Training: [+ Add] [History] [Export]).
  useSetBottomBarActions([
    {
      key: 'training-add',
      icon: PlusCircle,
      label: 'Add',
      primary: true,
      onClick: onAddTraining,
    },
    {
      key: 'training-history',
      icon: Clock,
      label: 'History',
      onClick: () => handleQuickAction('history'),
    },
    {
      key: 'training-export',
      icon: Download,
      label: 'Export',
      onClick: () => handleQuickAction('export'),
    },
  ]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Main Content - 2/3 width */}
      <div className="lg:col-span-2 pb-20 lg:pb-0">
        <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          {/* Header with slate-600 background and search inside */}
          <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
            <div className="flex items-center justify-between gap-6">
              {/* Title */}
              <h3 className="text-base font-semibold text-white whitespace-nowrap flex-shrink-0">
                Onboard Training
              </h3>

              {/* Search Input */}
              <div className="relative flex-1 max-w-2xl">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search training records..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-white text-slate-800 text-sm border border-white/20 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200"
                />
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="px-6 py-5">
            {/* Filters */}
            <div className="space-y-3 mb-6">
              {/* Row 1: Filter Dropdowns */}
              <div className="flex gap-3">
                {/* Category Filter Dropdown */}
                <Dropdown
                  value={categoryFilter}
                  onChange={setCategoryFilter}
                  width="flex-1 sm:flex-none sm:w-48"
                  options={[
                    { value: '', label: `All Categories (${trainingRecords.length})` },
                    ...Object.values(TRAINING_CATEGORIES)
                      .filter(cat => categoryCounts[cat.id] > 0)
                      .map(cat => ({
                        value: cat.id,
                        label: `${cat.label} (${categoryCounts[cat.id] || 0})`
                      }))
                  ]}
                />

                {/* Topic Filter Dropdown */}
                <Dropdown
                  value={topicTab}
                  onChange={setTopicTab}
                  width="flex-1 sm:flex-none sm:w-40"
                  options={[
                    { value: 'all', label: 'All Topics' },
                    ...trainingTopics.map(topic => ({
                      value: topic,
                      label: topic
                    }))
                  ]}
                />

                {/* Date Range Filter */}
                <Dropdown
                  value={dateRange}
                  onChange={setDateRange}
                  width="flex-1 sm:flex-none sm:w-40"
                  options={[
                    { value: 'all', label: 'All Time' },
                    { value: '30', label: 'Last 30 Days' },
                    { value: '90', label: 'Last 3 Months' },
                    { value: '180', label: 'Last 6 Months' },
                    { value: '365', label: 'Last Year' }
                  ]}
                />
              </div>
            </div>
          {/* Training Records List - Flat list matching Drill History */}
          <div className="divide-y divide-gray-200">
            {paginatedRecords.length === 0 ? (
              <div className="px-6 py-8 text-center">
                <GraduationCap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">
                  {hasActiveFilters
                    ? 'No training records match your filters'
                    : 'No training records found'}
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="mt-2 text-sm text-cyan-600 hover:text-cyan-500"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              paginatedRecords.map(record => (
                <div
                  key={record.id}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => onViewTraining(record)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      {/* Training icon - matches Equipment Register style */}
                      <GraduationCap className="h-6 w-6 text-cyan-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {record.topic}
                        </p>
                        <div className="flex items-center mt-1 space-x-4 text-sm text-gray-500">
                          <span className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {new Date(record.date).toLocaleDateString()}
                          </span>
                          <span className="flex items-center">
                            {record.trainees?.length > 1 ? (
                              <Users className="h-4 w-4 mr-1" />
                            ) : (
                              <User className="h-4 w-4 mr-1" />
                            )}
                            {record.trainees?.length || 0} trainee{record.trainees?.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Badge color="green">
                      Completed
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination - match Drill History exactly */}
          {totalPages > 1 && (
            <div className="pt-4 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-700">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, filteredRecords.length)} of{' '}
                {filteredRecords.length} results
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Sidebar - 1/3 width - Hidden on mobile */}
      <div className="hidden lg:block">
        <TrainingSidebar
          trainingRecords={trainingRecords}
          stats={stats}
          onQuickAction={handleQuickAction}
        />
      </div>

      {/* FT-358: mobile bottom-bar actions registered via
          useSetBottomBarActions above — the shell owns the bar. */}
    </div>
  );
};

export default TrainingRecordsTab;